import { isQaModeAllowedHost } from "./hostAllowlist";

/**
 * QA-only, in-memory (never localStorage) overrides for Instant Play &
 * Onboarding V1's player-state gates - lets a tester force a specific
 * onboarding state via URL query params, on an allowed QA host, without
 * ever touching real `OnboardingStorage`/`TutorialStorage`/`ProgressStorage`.
 * Read fresh on every call (not one-shot-consumed like `challengeRoute.ts`'s
 * route detection) so reloading the same QA URL keeps reproducing the same
 * forced state. Every value is `null` on the real production hostname -
 * see `isQaModeAllowedHost`.
 */

/**
 * `?qaOnboarding=` override for the CHOOSE YOUR BITE state:
 * - `"new"` - not yet completed (a genuinely new player).
 * - `"returning"` - already completed (still subject to Endless's own
 *   always-on ready gate, and to Campaign/Daily/Beat My Score's own
 *   ready gate on a genuinely new player's first run, both independent
 *   of this flag).
 */
export type OnboardingQaOverride = "new" | "returning";

export function resolveOnboardingQaOverride(hostname: string | null | undefined, search: string): OnboardingQaOverride | null {
  if (!isQaModeAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qaOnboarding");
  return raw === "new" || raw === "returning" ? raw : null;
}

/** `null` means "no override - read real `TutorialStorage`". */
export function qaOverrideBiteTutorialSeen(hostname: string | null | undefined, search: string): boolean | null {
  const override = resolveOnboardingQaOverride(hostname, search);
  if (override === null) return null;
  return override === "returning";
}

/**
 * `?qaInputMode=desktop|mobile` - forces which ready-prompt copy variant
 * shows, regardless of the real device's touch capability.
 */
export function resolveQaInputModeOverride(hostname: string | null | undefined, search: string): "desktop" | "mobile" | null {
  if (!isQaModeAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qaInputMode");
  return raw === "desktop" || raw === "mobile" ? raw : null;
}

/**
 * `?qaWorldsDiscovery=pending|shown` - forces whether an Endless/Daily/
 * Beat My Score result screen treats the worlds-discovery promotion as
 * not-yet-shown (`"pending"`) or already-shown (`"shown"`), without
 * touching the real `OnboardingStorage` flag - lets QA repeatedly preview
 * the discovery UI, or repeatedly preview its absence, on demand.
 */
export function resolveQaWorldsDiscoveryOverride(hostname: string | null | undefined, search: string): "pending" | "shown" | null {
  if (!isQaModeAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qaWorldsDiscovery");
  return raw === "pending" || raw === "shown" ? raw : null;
}

/**
 * `?qaEndlessGoalHint=pending|shown` - forces whether an Endless run treats
 * the one-time "GROW AS LONG AS YOU CAN" goal hint as not-yet-shown
 * (`"pending"`) or already-shown (`"shown"`), without touching the real
 * `OnboardingStorage` flag - lets QA repeatedly preview the hint, or
 * repeatedly preview its absence, on demand.
 */
export function resolveQaEndlessGoalHintOverride(hostname: string | null | undefined, search: string): "pending" | "shown" | null {
  if (!isQaModeAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qaEndlessGoalHint");
  return raw === "pending" || raw === "shown" ? raw : null;
}
